import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import backend from '../../services/backend';
import { useToast } from '../../components/Toast';

type ActionKind = 'reclassify' | 'relabel' | 'remove';

export function useClusterMemberActions({
  clusterId,
  categoryId,
  categoryName,
  selectedIds,
  onDone,
}: {
  clusterId: string;
  categoryId: number | undefined;
  categoryName: string | undefined;
  selectedIds: Set<string>;
  /** Clears the selection after a completed action. The member list itself refreshes
   * via query invalidation — the detail endpoint filters to live category membership,
   * so relabeled/removed pages drop out on refetch. */
  onDone: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<ActionKind | null>(null);
  const [dirty, setDirty] = useState(false);

  // Clear the "training set changed" banner when switching clusters (prev/next
  // reuses the page with a new clusterId without remounting).
  useEffect(() => {
    setDirty(false);
  }, [clusterId]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['categoryCluster', clusterId] });
    if (categoryId != null) {
      queryClient.invalidateQueries({ queryKey: ['categoryClusters', categoryId.toString()] });
      // Relabelling members moves the cluster centroid, so the server-computed
      // outlier geometry is stale too — it keys separately since DR-2809.
      queryClient.invalidateQueries({ queryKey: ['categoryClusterOutliers', categoryId.toString()] });
    }
  }, [queryClient, clusterId, categoryId]);

  const run = useCallback(
    async (kind: ActionKind, task: (id: string) => Promise<unknown>, successNoun: string, leavesCluster: boolean) => {
      const ids = [...selectedIds];
      if (ids.length === 0) return;
      setPending(kind);
      const succeededIds: string[] = [];
      try {
        // Sequential to keep the curation 409 stale-guard ordering predictable.
        for (const id of ids) {
          await task(id);
          succeededIds.push(id);
        }
        toast.success(`${successNoun} ${ids.length} page${ids.length === 1 ? '' : 's'}`);
      } catch {
        toast.error(
          succeededIds.length > 0
            ? `${successNoun} ${succeededIds.length} of ${ids.length}; the rest failed — please retry`
            : `Failed to ${kind} — please try again`,
        );
      } finally {
        // Any server-side change (even partial) must refresh the cache and clear the
        // selection so the UI doesn't show stale members or re-submit applied changes.
        if (succeededIds.length > 0) {
          // Only relabel/remove change the training set; reclassify just reruns
          // inference, so it must not raise the "training set changed" banner.
          if (leavesCluster) setDirty(true);
          invalidate();
          onDone();
        }
        setPending(null);
      }
    },
    [selectedIds, toast, invalidate, onDone],
  );

  const reclassify = useCallback(
    () => run('reclassify', (id) => backend.pageRetry(id, 'classification'), 'Reclassified', false),
    [run],
  );

  const relabel = useCallback(
    (target: string) =>
      run(
        'relabel',
        (id) =>
          backend.postCurationReview({
            pageId: id,
            verdict: 'corrected',
            expectedCategory: categoryName ?? null,
            newCategory: target,
            source: 'cluster_page',
          }),
        'Relabeled',
        true,
      ),
    [run, categoryName],
  );

  const remove = useCallback(
    () =>
      run(
        'remove',
        (id) =>
          backend.postCurationReview({
            pageId: id,
            verdict: 'corrected',
            expectedCategory: categoryName ?? null,
            newCategory: null,
            source: 'cluster_page',
          }),
        'Removed from category',
        true,
      ),
    [run, categoryName],
  );

  return { reclassify, relabel, remove, pending, dirty };
}
